import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { ArrowUpRight, Ban } from "@/components/icons";

const initials = (n) =>
  (n || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const money = (n) => `$${(n || 0).toFixed(2)}`;

export default function AdminPharmacyPayments() {
  const [data, setData] = useState({ pharmacies: [], doctors: [] });

  const load = () =>
    api.get("/users/billing/summary").then((r) => setData(r.data));

  useEffect(() => {
    load();
    // Refresh every 5s so admin sees changes in near real-time
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const totals = {
    pendingPharm: data.pharmacies.reduce((a, p) => a + (p.pending || 0), 0),
    pendingDoc: data.doctors.reduce((a, d) => a + (d.pending || 0), 0),
    paidPharm: data.pharmacies.reduce((a, p) => a + (p.paid || 0), 0),
  };

  return (
    <div className="space-y-6" data-testid="admin-pharmacy-payments">
      <div>
        <h1 className="text-3xl font-extrabold text-black tracking-tight">
          Pagos de farmacias
        </h1>
        <p className="text-sm text-black/60">
          Saldos en tiempo real por farmacia y por doctor — clic en un nombre
          para ver detalles, pagar o bloquear.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Metric
          title="Pendiente cobrar a farmacias"
          value={money(totals.pendingPharm)}
          tone="amber"
        />
        <Metric
          title="Pendiente pagar a doctores"
          value={money(totals.pendingDoc)}
          tone="blue"
        />
        <Metric
          title="Cobrado a farmacias"
          value={money(totals.paidPharm)}
          tone="emerald"
        />
      </div>

      <ListCard
        title="Farmacias"
        subtitle={`${data.pharmacies.length} registrada${data.pharmacies.length === 1 ? "" : "s"} · nos deben pagar`}
        rows={data.pharmacies}
        toBase="/admin/pagos-farmacia/farmacia"
        pendingLabel="Deben pagarnos"
        pendingTone="text-amber-700"
        testid="pharmacies-list"
      />

      <ListCard
        title="Doctores"
        subtitle={`${data.doctors.length} con ventas · les debemos pagar`}
        rows={data.doctors}
        toBase="/admin/pagos-farmacia/doctor"
        pendingLabel="Les debemos"
        pendingTone="text-black"
        testid="doctors-list"
      />
    </div>
  );
}

function ListCard({ title, subtitle, rows, toBase, pendingLabel, pendingTone, testid }) {
  return (
    <div className="bg-white rounded-[28px] border border-black/[0.06] p-6" data-testid={testid}>
      <div className="mb-4">
        <div className="text-lg font-extrabold text-black">{title}</div>
        <div className="text-xs text-black/55">{subtitle}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              {["Nombre", "Email", "Ventas", "Volumen", pendingLabel, "Pagado", ""].map(
                (h, i) => (
                  <th
                    key={h || `c${i}`}
                    className="py-3 px-3 first:pl-0 last:pr-0 text-[11px] uppercase tracking-widest text-[#9CA3AF] font-medium"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr
                key={u.id}
                data-testid={`row-${u.id}`}
                className="border-t border-[#E5E7EB] text-black hover:bg-black/[0.03] transition"
              >
                <td className="py-4 pr-3">
                  <Link
                    to={`${toBase}/${u.id}`}
                    className="flex items-center gap-3 group"
                  >
                    <div className="w-9 h-9 rounded-full bg-white border border-[#E5E7EB] text-black text-xs font-extrabold flex items-center justify-center flex-shrink-0">
                      {initials(u.name)}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold truncate max-w-[220px]">
                          {u.name}
                        </span>
                        {u.blocked && (
                          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-extrabold">
                            <Ban className="w-2.5 h-2.5" /> Bloq.
                          </span>
                        )}
                      </div>
                      {u.phone && (
                        <div className="text-[11px] text-black/55">{u.phone}</div>
                      )}
                    </div>
                  </Link>
                </td>
                <td className="py-4 px-3 text-black/85 truncate max-w-[220px]">
                  {u.email}
                </td>
                <td className="py-4 px-3 text-black/85 whitespace-nowrap">
                  {u.sales}
                </td>
                <td className="py-4 px-3 text-black/85 whitespace-nowrap">
                  {money(u.volume)}
                </td>
                <td className={`py-4 px-3 font-extrabold whitespace-nowrap ${pendingTone}`}>
                  {money(u.pending)}
                </td>
                <td className="py-4 px-3 text-[#019A75] font-bold whitespace-nowrap">
                  {money(u.paid)}
                </td>
                <td className="py-4 pl-3 text-right">
                  <Link
                    to={`${toBase}/${u.id}`}
                    data-testid={`open-${u.id}`}
                    className="inline-flex items-center gap-1 text-xs font-bold text-black/70 hover:text-black px-2.5 py-1.5 rounded-xl hover:bg-black/[0.05] transition"
                  >
                    Ver <ArrowUpRight className="w-3 h-3" />
                  </Link>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-black/55 py-10">
                  Sin registros
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Metric({ title, value, tone }) {
  const tones = {
    amber: "text-amber-700",
    blue: "text-black",
    emerald: "text-[#019A75]",
  };
  return (
    <div className="bg-white rounded-[28px] border border-black/[0.06] p-5">
      <div className="text-[11px] uppercase tracking-widest font-bold text-black/55">
        {title}
      </div>
      <div className={`text-3xl font-extrabold mt-2 ${tones[tone] || "text-black"}`}>
        {value}
      </div>
    </div>
  );
}
